/**
 * <PlayersPanel>: V4.2.T9 (note 5f + funnel).
 *
 * The drawing-phase LEFT column. A VERTICAL variant of the lobby
 * <SpectatorList>: it REUSES the shared `.spectator-list` / `.spectator-row`
 * base classes (so FreeDrawRoom and the in-game panel share avatar + name
 * chrome) and layers the in-game behavior via rules SCOPED under
 * `.drawing-phase-shell`: the base rules stay byte-unchanged (C8).
 *
 * Each row is a grid: [avatar] | [name (1fr, ellipsis)] | [score (tabular)].
 *   - The ACTIVE DRAWER row gets a LEFT ACCENT BAR (`data-drawer`), not a
 *     pill/badge swap: the bar reads at a glance without reflowing the row.
 *   - The "me" row highlight uses the NEW accent palette (`data-me`), NOT the
 *     retired --highlighter yellow (that override is scoped, so the lobby
 *     SpectatorList keeps its own styling).
 *
 * The persistent IN-GAME INVITE chip lives at the BOTTOM: it REUSES
 * <ShareLinkCTA> (the 3-strike rule, no fork), which already leads with the
 * 4-char room code. This is the in-game funnel surface during wordPick +
 * drawing.
 *
 * Handles the 2-player case (exactly one non-drawer row), nothing assumes 3+.
 */

import type { SerializedPlayer } from "@drawlol/shared";
import { ShareLinkCTA } from "./ShareLinkCTA.js";
import { CountUp } from "../anim/count-up.js";

export type PlayersPanelProps = {
  /** Full roster (carries each player's avatar `color`). */
  players: ReadonlyArray<SerializedPlayer>;
  /** The active drawer's id: gets the left accent bar. */
  drawerId: string;
  /** Local player's id: gets the "me" accent highlight. */
  myId: string | null;
  /** Per-player running score (playerId → total) from completed turns. */
  scores: ReadonlyMap<string, number>;
  /** Room code: drives the bottom <ShareLinkCTA> invite chip. */
  roomId: string;
};

export function PlayersPanel({ players, drawerId, myId, scores, roomId }: PlayersPanelProps) {
  return (
    <aside class="spectator-list players-panel" aria-label="Players">
      <ul class="spectator-list__items" role="list">
        {players.map((player) => (
          <PlayerRow
            key={player.id}
            player={player}
            isDrawer={player.id === drawerId}
            isMe={player.id === myId}
            score={scores.get(player.id) ?? 0}
          />
        ))}
      </ul>
      <div class="players-panel__invite" data-testid="players-panel-invite">
        <ShareLinkCTA roomId={roomId} />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// PlayerRow
// ---------------------------------------------------------------------------

type PlayerRowProps = {
  player: SerializedPlayer;
  isDrawer: boolean;
  isMe: boolean;
  score: number;
};

function PlayerRow({ player, isDrawer, isMe, score }: PlayerRowProps) {
  return (
    <li
      class="spectator-row"
      data-player-id={player.id}
      data-drawer={isDrawer ? "true" : undefined}
      data-me={isMe ? "true" : undefined}
      aria-label={buildAriaLabel(player.name, isDrawer, isMe, score)}
    >
      <span
        class="spectator-row__avatar"
        style={`background-color:${player.color}`}
        aria-hidden="true"
      />
      <span class="spectator-row__name">{player.name}</span>
      <span class="spectator-row__score" data-testid="player-row-score" aria-hidden="true">
        {/* Audit 2026-06-11: the panel used to PATCH instantly while the
            reveal card's CountUp was still mid-animation, two surfaces
            disagreed for ~1s at the exact moment players audit their score.
            Same glide on both. animateOnMount=false (review P3): the panel
            remounts on every phase swap: without it, all totals re-rolled
            0→N each turn. Changes still glide. */}
        <CountUp value={score} animateOnMount={false} />
      </span>
    </li>
  );
}

function buildAriaLabel(name: string, isDrawer: boolean, isMe: boolean, score: number): string {
  const parts = [name];
  if (isDrawer) parts.push("(drawing)");
  if (isMe) parts.push("(you)");
  parts.push(`${score} points`);
  return parts.join(" ");
}
